import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

import CustomDualListBox from "../CustomDualListbox";
import { getEnvEndpoints } from "../../utils/functions";

/**
 * Dual-list picker for adding/removing followed municipalities and provinces.
 */
const MunicipalityProvincePicker = ({
  selectedMunicipalityIds,
  selectedProvinceIds,
  homeMunicipalityId,
  lang,
  translate,
  onSaved,
  createNotification,
}) => {
  const env = getEnvEndpoints();

  const [allMunicipalities, setAllMunicipalities] = useState([]);
  const [allProvinces, setAllProvinces] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Local edit state — what the user has staged but not yet saved.
  const [stagedMunicipalityIds, setStagedMunicipalityIds] = useState(
    selectedMunicipalityIds || []
  );
  const [stagedProvinceIds, setStagedProvinceIds] = useState(
    selectedProvinceIds || []
  );

  // Reset local state when props change (e.g. after a save → parent refreshes Redux).
  useEffect(() => {
    setStagedMunicipalityIds(selectedMunicipalityIds || []);
  }, [selectedMunicipalityIds]);
  useEffect(() => {
    setStagedProvinceIds(selectedProvinceIds || []);
  }, [selectedProvinceIds]);

  // Fetch the option lists once on mount — same endpoints registration dialog uses.
  useEffect(() => {
    const fetchOptions = async () => {
      const token = localStorage.getItem("StadGemeenteToken");
      const headers = token
        ? { "Content-Type": "application/json", Authorization: `Token ${token}` }
        : { "Content-Type": "application/json" };
      setOptionsLoading(true);
      try {
        const [muniRes, provRes] = await Promise.all([
          axios.get(
            `${env.REACT_APP_BASE_MEDIA_URL}/core/new-api/geo/municipality/?fields=id,name_nl,name_fr,name_de,name_en&paginate=false&ordering=name_nl,name_fr,name_de,name_en`,
            { headers }
          ),
          axios.get(
            `${env.REACT_APP_BASE_MEDIA_URL}/core/new-api/geo/province/?fields=id,name&paginate=false&ordering=name`,
            { headers }
          ),
        ]);
        setAllMunicipalities(muniRes.data || []);
        setAllProvinces(provRes.data || []);
      } catch (err) {
        if (createNotification) {
          createNotification(
            "error",
            translate
              ? translate("publications_load_error") || "Kon gegevens niet laden."
              : "Kon gegevens niet laden."
          );
        }
      } finally {
        setOptionsLoading(false);
      }
    };
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env.REACT_APP_BASE_MEDIA_URL]);

  // Build option arrays for CustomDualListBox.
  // Name fallback matches Login.jsx:745-754 — prefer NL/FR combined, then NL, FR, DE, EN.
  const municipalityOptions = useMemo(() => {
    const opts = (allMunicipalities || []).map((item) => {
      let name =
        (item.name_nl && item.name_fr && `${item.name_nl} / ${item.name_fr}`) ||
        item.name_nl ||
        item.name_fr ||
        item.name_de ||
        item.name_en ||
        "Unknown";
      if (name.toLowerCase().startsWith("'s")) {
        name = name.replace(/^'s\s*/i, "");
      }
      return { value: item.id, label: name };
    });
    opts.sort((a, b) => a.label.localeCompare(b.label));
    return opts;
  }, [allMunicipalities]);

  const provinceOptions = useMemo(() => {
    return (allProvinces || []).map((p) => ({ value: p.id, label: p.name }));
  }, [allProvinces]);

  const handleMunicipalityChange = (newValues) => {
    setStagedMunicipalityIds(newValues);
  };

  const handleProvinceChange = (newValues) => {
    setStagedProvinceIds(newValues);
  };

  const handleSave = async () => {
    const token = localStorage.getItem("StadGemeenteToken");
    if (!token) return;
    setSaving(true);
    try {
      await axios.post(
        `${env.REACT_APP_BASE_MEDIA_URL}/core/api/stadgemeente/follow-overview/update/?lang=${lang}`,
        {
          municipality_ids: stagedMunicipalityIds,
          province_ids: stagedProvinceIds,
        },
        { headers: { Authorization: `Token ${token}` } }
      );
      if (onSaved) {
        await onSaved();
      }
      if (createNotification) {
        createNotification(
          "success",
          translate
            ? translate("selections_saved") || "Selecties opgeslagen"
            : "Selecties opgeslagen"
        );
      }
    } catch (err) {
      if (createNotification) {
        createNotification(
          "error",
          translate
            ? translate("selections_save_error") || "Opslaan mislukt"
            : "Opslaan mislukt"
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (optionsLoading) {
    return (
      <div className="follow-picker follow-picker--loading">
        {translate ? translate("loading") || "Laden..." : "Laden..."}
      </div>
    );
  }

  return (
    <div className="follow-picker">
      <div className="follow-picker__columns">
        <div className="follow-picker__column">
          <h3 className="follow-picker__heading">
            {translate ? translate("follow_municipalities") || "Gemeenten volgen" : "Gemeenten volgen"}
          </h3>
          <CustomDualListBox
            options={municipalityOptions}
            selected={stagedMunicipalityIds}
            onChange={handleMunicipalityChange}
            disabledValues={homeMunicipalityId ? [homeMunicipalityId] : []}
          />
        </div>
        <div className="follow-picker__column">
          <h3 className="follow-picker__heading">
            {translate ? translate("follow_provinces") || "Provincies volgen" : "Provincies volgen"}
          </h3>
          <CustomDualListBox
            options={provinceOptions}
            selected={stagedProvinceIds}
            onChange={handleProvinceChange}
          />
        </div>
      </div>

      <div className="follow-picker__hint">
        {translate
          ? translate("multi_select_ctrl") || "Meerdere selectie mogelijk (CTRL)"
          : "Meerdere selectie mogelijk (CTRL)"}
      </div>

      <div className="follow-picker__actions">
        <button
          type="button"
          className="follow-picker__save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving
            ? translate
              ? translate("saving") || "Opslaan..."
              : "Opslaan..."
            : translate
            ? translate("save_selections") || "Selecties opslaan"
            : "Selecties opslaan"}
        </button>
      </div>
    </div>
  );
};

export default MunicipalityProvincePicker;
